import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface SubmitFeedbackParams {
  feedbackType: 'bug' | 'feature' | 'wrong_info' | 'other';
  feedbackText: string;
  contactEmail: string;
  rating?: number;
}

interface FeedbackSubmitResult {
  success: boolean;
  message?: string;
  error?: string;
}

export const useFeedbackSubmit = () => {
  const { session } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submitFeedback = useCallback(
    async (params: SubmitFeedbackParams): Promise<FeedbackSubmitResult> => {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);

      try {
        const token = session?.access_token;
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/api/feedback`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            feedbackType: params.feedbackType,
            feedbackText: params.feedbackText,
            contactEmail: params.contactEmail,
            rating: params.rating,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error?.message || 'Failed to submit feedback');
        }

        setSuccess(data.data?.message || 'Feedback submitted successfully!');

        return {
          success: true,
          message: data.data?.message,
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to submit feedback';
        setError(errorMessage);
        return {
          success: false,
          error: errorMessage,
        };
      } finally {
        setIsSubmitting(false);
      }
    },
    [session]
  );

  const clearError = useCallback(() => setError(null), []);
  const clearSuccess = useCallback(() => setSuccess(null), []);

  return {
    submitFeedback,
    isSubmitting,
    error,
    success,
    clearError,
    clearSuccess,
  };
};
